// World roles (DOR-6 amended by EDU-5, ARCH §29.7). The one rank table every role gate
// reads — the server's ACL (`server/worlds.ts` re-exports these), the instructor's
// console, and the co-working share policy (§30.6) — so "who outranks whom" is a single
// fact rather than three copies that can drift. Pure: no I/O, no storage, no DOM.

/** Access role on a world: `owner` created it; `instructor` runs lessons in it (a
 *  real role, not an op — D-TF-145); `editor` may build; `visitor` may enter + look. */
export type Role = 'owner' | 'instructor' | 'editor' | 'visitor';

/** Every role, highest first (`owner > instructor > editor > visitor`). */
export const ROLES: readonly Role[] = ['owner', 'instructor', 'editor', 'visitor'];

const RANK: Readonly<Record<Role, number>> = { owner: 3, instructor: 2, editor: 1, visitor: 0 };

/** A role's rank — higher outranks lower. */
export function rankOf(role: Role): number {
  return RANK[role];
}

/** Whether `role` is at least `min`. A missing role (no access) is below everything. */
export function atLeast(role: Role | null | undefined, min: Role): boolean {
  return role != null && RANK[role] >= RANK[min];
}

/** Whether `s` names a role (an ACL row read back from storage is untrusted data). */
export function isRole(s: unknown): s is Role {
  return typeof s === 'string' && Object.prototype.hasOwnProperty.call(RANK, s);
}

/** A guest subject (`guest:<name>:<ticket>`, `server/auth.ts`) — minted per invite,
 *  never an OIDC identity, so it can never hold `instructor` (D-TF-145, D-TF-147). */
export function isGuestSubject(sub: string): boolean {
  return sub.startsWith('guest:');
}

/** The solo player's role (§29.7): the instructor whenever cheats are on — today's
 *  CLS/F9 gate, unchanged — else an editor of their own world. */
export function soloRole(cheats: boolean): Role {
  return cheats ? 'instructor' : 'editor';
}

/** The binding refusal for `/instructor` on a guest (§29.7). */
export const GUEST_INSTRUCTOR_REFUSAL = 'Guests cannot be instructors.';
